import { MapDataState } from '../definitions/map-data-state.service';
import type { Observable } from 'rxjs';
import { BehaviorSubject, catchError, of, startWith, switchMap } from 'rxjs';
import type { FilterModel } from '@ui/shared/util-filter';
import { ApiDataStatus, fallbackIfDelayed } from '@ui/shared/util-api';
import type { Result } from '@team23/ts-result';
import { toDataResult, ToResult } from '@team23/ts-result';

export type MapDataResult<Item> = Result<Array<Item>, ApiDataStatus>;

/**
 * base map data state service to encapsulate reactivity
 */
export abstract class BaseMapDataStateService<Item> extends MapDataState<Item> {

    protected readonly _reload$ = new BehaviorSubject<null>(null);

    public abstract override items$({ filters }: {
        filters: Map<string, FilterModel>;
    }): Observable<MapDataResult<Item>>;

    public reloadMapItems(): void {
        this._reload$.next(null);
    }

    public getData$(data$: Observable<Array<Item>>): Observable<MapDataResult<Item>> {
        return this._reload$
            .pipe(
                switchMap(() =>
                    fallbackIfDelayed(
                        data$
                            .pipe(
                                toDataResult(),
                                // Catch the error within the switchmap to keep the outer observable from closing.
                                catchError(error => {
                                    console.error(error);

                                    return of(ToResult.fromErrorKey(ApiDataStatus.GenericError));
                                }),
                            ),
                        ToResult.fromErrorKey(ApiDataStatus.Loading),
                    ),
                ),
                startWith(ToResult.fromErrorKey(ApiDataStatus.Loading)),
            );
    }
}
